import React, {
  useContext, useEffect, useRef, useState,
} from 'react';
import {
  StyleSheet, SectionList, ScrollView, RefreshControl, useWindowDimensions,
} from 'react-native';
import {
  Avatar,
  Banner,
  Text,
  IconButton,
} from 'react-native-paper';
import { useMutation, useQuery } from '@apollo/client';
import {
  Checkbox, useToast, View,
} from 'native-base';
import { FindManyEvents } from '../../gql/event/query';
import {
  ROLE_NAMES,
} from '../../utils/constant';
import { FindManyAnnouncements } from '../../gql/announcement/query';
import { AcknowledgeAnnouncement } from '../../gql/announcement/mutation';
import { AuthContext } from '../../context/AuthContext';
import { computeCategorie } from '../../utils/utils';
import { FindOneUserQuery } from '../../gql/user/query';
import { ModalContext } from '../../context/ModalContext';
import InformationModal from '../../modals/Information/InformationModal';
import PlayersOnEventModal from '../../modals/ManageEvent/DisplayPlayersOnEventModal';
import ManageFiltersModal from '../../modals/ManageFilters/index';
import PictureModal from '../../modals/Picture/PictureModal';
import EventCard from './EventCardView/EventCardView';
import SelectSeason from '../../components/commons/SelectSeason';
import EventAchievementModal from '../../modals/ManageAchievements/DisplayEventAchievementsModal';
import LoadingIndicator from '../../components/commons/LoadingIndicator';
import useDesktopLayout from '../../hooks/useDesktopLayout';

const newStyles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#f8f9fa',
  },
  header: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingHorizontal: 16,
    paddingVertical: 10,
    backgroundColor: 'white',
    borderBottomWidth: 1,
    borderBottomColor: '#e9ecef',
  },
  sectionHeader: {
    paddingHorizontal: 16,
    paddingVertical: 10,
    backgroundColor: '#f8f9fa',
    paddingBottom: 10,
  },
  emptyContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    marginTop: 50,
  },
  loadingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
  },
});

function Dashboard() {
  const { authState } = useContext(AuthContext);
  const toast = useToast();
  const refEventId = useRef(undefined);
  const refAchievementEventId = useRef(undefined);
  const isDesktop = useDesktopLayout();
  const { width } = useWindowDimensions();
  const [gridWidth, setGridWidth] = useState(0);
  const GRID_GAP = 16;
  let gridColumns = 1;
  let cardWidth;
  if (isDesktop) {
    const contentWidth = (gridWidth || Math.min(width, 1400)) - 32;
    gridColumns = Math.max(2, Math.min(3, Math.floor(contentWidth / 420)));
    cardWidth = (contentWidth - (gridColumns - 1) * GRID_GAP) / gridColumns;
  }

  const [today] = useState(new Date());
  const [isRefreshing, setIsRefreshing] = useState(false);

  const [visibleSections, setVisibleSections] = useState(['Derniers jours', 'À venir']);
  const [selectedSeason, setSelectedSeason] = useState(undefined);
  const [isFilterOpen, setIsFilterOpen] = useState(false);

  const skipScheduledEvents = !visibleSections.includes('Derniers jours') && !visibleSections.includes('À venir');
  const skipCompletedEvents = !visibleSections.includes('Terminés');

  const {
    data: announcementData,
    loading: announcementLoading,
    error: announcementError,
    refetch: announcementRefresh,
  } = useQuery(
    FindManyAnnouncements,
    {
      variables: {
        endDate: today,
      },
    },
  );
  const [acknownledgeAnnouncement] = useMutation(AcknowledgeAnnouncement);

  const {
    displayInformationModal,
    displayPlayersOnEventModal, togglePlayersOnEventModal,
    displayPictureModal,
    displayFiltersModal,
    displayEventAchievementsModal,
  } = useContext(ModalContext);
  const {
    data: oneUserQuery,
    loading: oneUserQueryLoading,
    error: oneUserQueryError,
    refetch: oneUserQueryRefetch,
  } = useQuery(FindOneUserQuery, {
    variables: {
      email: authState.email,
    },
  });

  const {
    data: scheduledEventsData,
    loading: scheduledEventsLoading,
    error: scheduledEventsError,
    refetch: scheduledEventsRefresh,
  } = useQuery(FindManyEvents, {
    variables: {
      status: 'SCHEDULED',
      seasonId: selectedSeason,
    },
    skip: skipScheduledEvents,
  });

  const {
    data: completedEventsData,
    loading: completedEventsLoading,
    error: completedEventsError,
    refetch: completedEventsRefresh,
  } = useQuery(FindManyEvents, {
    variables: {
      status: 'CLOSED',
      seasonId: selectedSeason,
    },
    skip: skipCompletedEvents,
  });

  const eventsData = {
    findManyEvents: [
      ...(scheduledEventsData?.findManyEvents || []),
      ...(completedEventsData?.findManyEvents || []),
    ],
  };
  const eventsError = (!skipScheduledEvents && scheduledEventsError) || (!skipCompletedEvents && completedEventsError);
  const eventsLoading = scheduledEventsLoading || completedEventsLoading;

  // Computed values
  const user = oneUserQuery?.findOneUser;
  const isSuperAdmin = authState.role === ROLE_NAMES.SUPERADMIN;
  const isAdmin = [ROLE_NAMES.COACH, ROLE_NAMES.SUPERADMIN].includes(authState.role);
  const userCategories = computeCategorie(new Date(user?.profile?.dateOfBirth), selectedSeason);

  const terminatedEvents = eventsData?.findManyEvents?.filter((event) => event.isTerminated);
  const lessThan14DaysEvents = eventsData?.findManyEvents?.filter((event) => event.lessThan14days);
  const otherEvents = eventsData?.findManyEvents?.filter((event) => !event.isTerminated && !event.lessThan14days);

  const eventSections = [
    { title: 'Derniers jours', data: lessThan14DaysEvents || [] },
    { title: 'À venir', data: otherEvents || [] },
    { title: 'Terminés', data: terminatedEvents || [] },
  ].filter((section) => section.data.length > 0 && visibleSections.includes(section.title));

  const handlePlayersOnEvent = (_eventId, action) => {
    if (isAdmin) {
      refEventId.current = Number(_eventId);
      togglePlayersOnEventModal({ action });
    }
  };

  const handleShowAchievements = (_eventId) => {
    refAchievementEventId.current = Number(_eventId);
  };

  useEffect(() => {
    if (announcementError || oneUserQueryError || eventsError) {
      toast.show({
        title: 'Erreur inattendue',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    }
  }, [announcementError, oneUserQueryError, eventsError]);

  const isLoading = announcementLoading || oneUserQueryLoading || eventsLoading;

  const onRefresh = async () => {
    setIsRefreshing(true);
    await Promise.all([
      announcementRefresh && announcementRefresh(),
      oneUserQueryRefetch && oneUserQueryRefetch(),
      scheduledEventsRefresh && scheduledEventsRefresh(),
      completedEventsRefresh && completedEventsRefresh(),
    ]);
    setIsRefreshing(false);
  };

  return (
    <>
      {announcementData?.findManyAnnouncements?.map((a) => (
        <Banner
          key={a.announcement.id}
          visible
          actions={[
            {
              label: 'J\'ai lu',
              onPress: () => {
                acknownledgeAnnouncement(
                  {
                    variables: {
                      acknowledgeAnnouncementId: a.announcement.id,
                      hasBeenRead: true,
                    },
                    refetchQueries: [
                      FindManyAnnouncements,
                      'findManyAnnouncements',
                    ],
                  },
                );
              },
            },
          ]}
          icon={() => (a.announcement.author.profile.pictureThumbnail
            ? (
              <Avatar.Image
                size={50}
                source={{
                  uri: a.announcement.author.profile.pictureThumbnail,
                }}
              />
            )
            : (
              <Avatar.Text
                size={50}
                label={`${a.announcement.author.firstName[0]}${a.announcement.author.lastName[0]}`}
              />
            ))}
        >
          <View>
            <Text variant="labelMedium">{a.announcement.title}</Text>
            <Text variant="labelSmall">{a.announcement.message}</Text>
          </View>
        </Banner>
      ))}
      <View style={[newStyles.header, { zIndex: 3000 }]}>
        <View style={{ zIndex: 3000 }}>
          <SelectSeason
            selectedSeason={selectedSeason}
            setSelectedSeason={setSelectedSeason}
          />
        </View>
        <IconButton
          icon="filter-variant"
          onPress={() => setIsFilterOpen(true)}
        />
      </View>
      {isFilterOpen && (
        <View style={{
          position: 'absolute',
          top: 60,
          right: 16,
          backgroundColor: 'white',
          borderRadius: 8,
          padding: 16,
          shadowColor: '#000',
          shadowOffset: { width: 0, height: 2 },
          shadowOpacity: 0.25,
          shadowRadius: 4,
          elevation: 5,
          zIndex: 5000,
        }}
        >
          <Text style={{ fontWeight: 'bold', marginBottom: 8 }}>Filtrer les évènements</Text>
          {['Derniers jours', 'À venir', 'Terminés'].map((sectionTitle) => (
            <Checkbox
              key={sectionTitle}
              value={sectionTitle}
              isChecked={visibleSections.includes(sectionTitle)}
              onChange={(isChecked) => {
                setVisibleSections((prevVisibleSections) => {
                  if (isChecked) {
                    return [...prevVisibleSections, sectionTitle];
                  }
                  return prevVisibleSections.filter((title) => title !== sectionTitle);
                });
              }}
              my={2}
            >
              {sectionTitle}
            </Checkbox>
          ))}
          <IconButton
            icon="close"
            size={20}
            onPress={() => setIsFilterOpen(false)}
            style={{ position: 'absolute', top: 8, right: 8 }}
          />
        </View>
      )}
      <View style={[newStyles.container, { flex: 1 }]}>
        {displayFiltersModal.display && <ManageFiltersModal />}
        {displayPictureModal.display && <PictureModal />}
        {displayInformationModal.display && <InformationModal />}
        {displayPlayersOnEventModal.display
          && <PlayersOnEventModal eventId={refEventId.current} />}
        {displayEventAchievementsModal.display
          && <EventAchievementModal eventId={refAchievementEventId.current} />}

        {isLoading && (
          <LoadingIndicator text="Chargement des évènements..." />
        )}
        {!isLoading && isDesktop && (
          <ScrollView
            style={{ flex: 1 }}
            contentContainerStyle={{ flexGrow: 1 }}
            refreshControl={(
              <RefreshControl refreshing={isRefreshing} onRefresh={onRefresh} />
            )}
          >
            <View
              onLayout={(e) => setGridWidth(e.nativeEvent.layout.width)}
              style={{ width: '100%', maxWidth: 1400, alignSelf: 'center' }}
            >
              {eventSections.length === 0 && (
                <View style={newStyles.emptyContainer}>
                  <Text>Aucun évènement pour le moment.</Text>
                </View>
              )}
              {eventSections.map(({ title, data }) => (
                <View key={title}>
                  <View style={newStyles.sectionHeader}>
                    <Text variant="headlineMedium" style={{ fontWeight: 'bold' }}>{title}</Text>
                  </View>
                  <View style={{
                    flexDirection: 'row', flexWrap: 'wrap', alignItems: 'stretch', gap: GRID_GAP, paddingHorizontal: 16,
                  }}
                  >
                    {data.map((item) => (
                      <View key={item.id} style={{ width: cardWidth, flexGrow: 0, flexShrink: 0 }}>
                        <EventCard
                          user={user}
                          event={item}
                          today={today}
                          isSuperAdmin={isSuperAdmin}
                          isAdmin={isAdmin}
                          userCategories={item.level === 'INTERNATIONAL' ? userCategories.international : userCategories.national}
                          handlePlayersOnEvent={handlePlayersOnEvent}
                          handleShowAchievements={handleShowAchievements}
                        />
                      </View>
                    ))}
                  </View>
                </View>
              ))}
            </View>
          </ScrollView>
        )}
        {!isLoading && !isDesktop && (
          <SectionList
            style={{ flex: 1 }}
            sections={eventSections}
            keyExtractor={(item) => item.id.toString()}
            renderItem={({ item }) => (
              <EventCard
                user={user}
                event={item}
                today={today}
                isSuperAdmin={isSuperAdmin}
                isAdmin={isAdmin}
                userCategories={item.level === 'INTERNATIONAL' ? userCategories.international : userCategories.national}
                handlePlayersOnEvent={handlePlayersOnEvent}
                handleShowAchievements={handleShowAchievements}
              />
            )}
            renderSectionHeader={({ section: { title } }) => (
              <View style={newStyles.sectionHeader}>
                <Text variant="headlineMedium" style={{ fontWeight: 'bold' }}>{title}</Text>
              </View>
            )}
            ListEmptyComponent={(
              <View style={newStyles.emptyContainer}>
                <Text>Aucun évènement pour le moment.</Text>
              </View>
            )}
            contentContainerStyle={{ flexGrow: 1 }}
            refreshControl={(
              <RefreshControl
                refreshing={isRefreshing}
                onRefresh={onRefresh}
              />
            )}
          />
        )}
      </View>
    </>
  );
}

export default Dashboard;
